import React, { useState, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    countryCode: '+20',
    nationality: '',
    role: 'client', // Guest registration only
  });
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { register, getDashboardPath } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const totalSteps = 5;

  const validateFirstName = (firstName) => {
    if (!firstName) {
      return t('common.nameRequired');
    }
    if (firstName.length < 2) {
      return t('common.nameMinLength');
    }
    if (/[0-9]/.test(firstName)) {
      return t('common.nameNoNumbers');
    }
    return '';
  };

  const validateLastName = (lastName) => {
    if (!lastName) {
      return t('common.nameRequired');
    }
    if (lastName.length < 2) {
      return t('common.nameMinLength');
    }
    if (/[0-9]/.test(lastName)) {
      return t('common.nameNoNumbers');
    }
    return '';
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return t('common.emailRequired');
    }
    if (!emailRegex.test(email)) {
      return t('common.emailInvalid');
    }
    return '';
  };

  const validatePassword = (password) => {
    if (!password) {
      return t('common.passwordRequired');
    }
    if (password.length < 6) {
      return t('common.passwordMinLength');
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) {
      return t('common.confirmPasswordRequired');
    }
    if (confirmPassword !== password) {
      return t('common.passwordsDoNotMatch');
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (phone && phone.trim() !== '') {
      const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
      if (!phoneRegex.test(phone)) {
        return t('common.phoneInvalid');
      }
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    
    // Clear error for this field when user starts typing
    const updatedErrors = { ...fieldErrors };
    if (updatedErrors[name]) {
      updatedErrors[name] = '';
    }
    
    // If password changes and confirmPassword has a value, re-validate confirmPassword
    if (name === 'password' && formData.confirmPassword) {
      updatedErrors.confirmPassword = validateConfirmPassword(formData.confirmPassword, value);
    }
    
    setFieldErrors(updatedErrors);
  };

  const validateCurrentStep = () => {
    let errors = {};
    
    switch (currentStep) {
      case 1:
        errors.firstName = validateFirstName(formData.firstName);
        errors.lastName = validateLastName(formData.lastName);
        break;
      case 2:
        errors.email = validateEmail(formData.email);
        break;
      case 3:
        errors.password = validatePassword(formData.password);
        errors.confirmPassword = validateConfirmPassword(formData.confirmPassword, formData.password);
        break;
      case 4:
        if (!formData.nationality.trim()) {
          errors.nationality = 'Nationality is required';
        }
        break;
      case 5:
        errors.phone = validatePhone(formData.phone);
        break;
      default:
        break;
    }
    
    setFieldErrors({ ...fieldErrors, ...errors });
    return !Object.values(errors).some(error => error !== '');
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    
    switch (name) {
      case 'firstName':
        error = validateFirstName(value);
        break;
      case 'lastName':
        error = validateLastName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'confirmPassword':
        error = validateConfirmPassword(value, formData.password);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'nationality':
        if (!value.trim()) {
          error = 'Nationality is required';
        }
        break;
      default:
        break;
    }
    
    setFieldErrors({ ...fieldErrors, [name]: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const firstNameError = validateFirstName(formData.firstName);
    const lastNameError = validateLastName(formData.lastName);
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    const confirmPasswordError = validateConfirmPassword(formData.confirmPassword, formData.password);
    const phoneError = validatePhone(formData.phone);
    const nationalityError = !formData.nationality.trim() ? 'Nationality is required' : '';

    if (firstNameError || lastNameError || emailError || passwordError || confirmPasswordError || phoneError || nationalityError) {
      setFieldErrors({
        firstName: firstNameError,
        lastName: lastNameError,
        email: emailError,
        password: passwordError,
        confirmPassword: confirmPasswordError,
        phone: phoneError,
        nationality: nationalityError
      });
      return;
    }

    setLoading(true);
    try {
      // Combine firstName and lastName into name for the API; backend expects dateOfBirth, gender
      const registrationData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        dateOfBirth: formData.dateOfBirth || '1990-01-01',
        gender: formData.gender || 'Male'
      };
      const result = await register(registrationData);
      // Backend returns no token — user must verify email first; show success and "Go to Sign in"
      if (result?.needsVerification || (result?.message && !localStorage.getItem('token'))) {
        setSignupSuccess(result?.message || 'Registration successful. Please verify your email to complete your registration.');
        return;
      }
      // If backend ever returns a token, redirect to dashboard
      if (location.state?.redirectTo && location.state?.redirectView) {
        navigate(location.state.redirectTo, { state: { view: location.state.redirectView } });
      } else {
        const roleMap = {
          super_admin: '/super-admin',
          admin: '/admin',
          operations: '/operations',
          property_owner: '/owner',
          client: '/client',
          housekeeping: '/housekeeping',
        };
        const dashboardPath = roleMap[result?.role] || '/client';
        navigate(dashboardPath, result?.role === 'client' ? { state: { view: 'saved' } } : undefined);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
      <Navbar />
      
      <div className="flex items-center justify-center min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Logo Section */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              {/* Logo */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="flex items-center justify-center p-1.5 rounded-lg" style={{ backgroundColor: '#FFFFFF' }}>
                  <img 
                    src="/hg-logo.png" 
                    alt="HOSTA GLOBAL Logo" 
                    className="w-auto"
                    style={{ maxHeight: '26px', objectFit: 'contain' }}
                  />
                </div>
                <div className="flex flex-col text-left">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold leading-tight uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>HOSTA</span>
                    <span className="text-2xl font-bold leading-tight uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>GLOBAL</span>
                  </div>
                  <span className="text-[10px] leading-tight uppercase tracking-wide" style={{ color: '#D5B577', opacity: 0.8 }}>ELEVATING ASSETS | REDEFINING STAYS</span>
                </div>
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{t('register.title')}</h1>
            <p className="text-gray-400">{t('common.signUpAsGuest')}</p>
          </div>

          {/* Auth Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border p-5 sm:p-6" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
            {signupSuccess ? (
              <div className="text-center py-4">
                <div className="mb-6 p-4 rounded-xl bg-green-500/20 border border-green-500/50 text-green-200 text-sm">
                  {signupSuccess}
                </div>
                <p className="text-gray-300 text-sm mb-4">
                  Check your email and click the verification link. Then sign in below.
                </p>
                <Link
                  to="/login"
                  className="inline-block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all"
                  style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                >
                  Go to Sign in
                </Link>
              </div>
            ) : (
              <>
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-300 text-sm flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            )}

            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                {[...Array(totalSteps)].map((_, index) => (
                  <div key={index} className="flex items-center flex-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                        index + 1 <= currentStep
                          ? 'bg-[#D5B577] text-[#1D2A3B]'
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      {index + 1 < currentStep ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </div>
                    {index < totalSteps - 1 && (
                      <div
                        className={`flex-1 h-1 mx-1 transition-all ${
                          index + 1 < currentStep ? 'bg-[#D5B577]' : 'bg-gray-600'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 text-center">
                Step {currentStep} of {totalSteps}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Step 1: First Name and Last Name */}
              {currentStep === 1 && (
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                    {t('common.firstName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t('common.enterFirstName')}
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: fieldErrors.firstName ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                        height: '42px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!fieldErrors.firstName) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                          e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                        }
                      }}
                      required
                    />
                  </div>
                  {fieldErrors.firstName && (
                    <p className="mt-0.5 text-xs text-red-400">{fieldErrors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                    {t('common.lastName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={t('common.enterLastName')}
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: fieldErrors.lastName ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                        height: '42px'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        if (!fieldErrors.lastName) {
                          e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                          e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                        }
                      }}
                      required
                    />
                  </div>
                  {fieldErrors.lastName && (
                    <p className="mt-0.5 text-xs text-red-400">{fieldErrors.lastName}</p>
                  )}
                </div>
              </div>
              )}

              {/* Step 2: Email */}
              {currentStep === 2 && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  {t('register.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.email ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      height: '42px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.email) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }
                    }}
              required
            />
          </div>
                {fieldErrors.email && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.email}</p>
                )}
              </div>
              )}

              {/* Step 3: Password and Confirm Password */}
              {currentStep === 3 && (
                <>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  {t('register.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
            <input
                    type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
                    placeholder="Create a password"
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.password ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      height: '42px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.password) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }
                    }}
              required
            />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  {t('common.reEnterPassword')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
            <input
                    type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
                    placeholder="Confirm your password"
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.confirmPassword ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      height: '42px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.confirmPassword) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }
                    }}
              required
            />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.confirmPassword}</p>
                )}
              </div>
                </>
              )}

              {/* Step 4: Nationality */}
              {currentStep === 4 && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  Nationality
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    className="w-full pl-10 pr-8 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 appearance-none text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.nationality ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      color: formData.nationality ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                      height: '42px',
                      backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%23D5B577\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.2em 1.2em',
                      paddingRight: '2rem'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.nationality) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }
                    }}
                    required
                  >
                    <option value="" disabled style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{t('common.selectNationality')}</option>
                    <option value="Egyptian">{t('common.nationalities.egyptian')}</option>
                    <option value="Saudi Arabian">{t('common.nationalities.saudiArabian')}</option>
                    <option value="Emirati">{t('common.nationalities.emirati')}</option>
                    <option value="Kuwaiti">{t('common.nationalities.kuwaiti')}</option>
                    <option value="Qatari">{t('common.nationalities.qatari')}</option>
                    <option value="Omani">{t('common.nationalities.omani')}</option>
                    <option value="Bahraini">{t('common.nationalities.bahraini')}</option>
                    <option value="Jordanian">{t('common.nationalities.jordanian')}</option>
                    <option value="Lebanese">{t('common.nationalities.lebanese')}</option>
                    <option value="Syrian">{t('common.nationalities.syrian')}</option>
                    <option value="Iraqi">{t('common.nationalities.iraqi')}</option>
                    <option value="Palestinian">{t('common.nationalities.palestinian')}</option>
                    <option value="Yemeni">{t('common.nationalities.yemeni')}</option>
                    <option value="Sudanese">{t('common.nationalities.sudanese')}</option>
                    <option value="Libyan">{t('common.nationalities.libyan')}</option>
                    <option value="Tunisian">{t('common.nationalities.tunisian')}</option>
                    <option value="Algerian">{t('common.nationalities.algerian')}</option>
                    <option value="Moroccan">{t('common.nationalities.moroccan')}</option>
                    <option value="American">American</option>
                    <option value="British">British</option>
                    <option value="Canadian">Canadian</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                    <option value="Italian">Italian</option>
                    <option value="Spanish">Spanish</option>
                    <option value="Australian">Australian</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {fieldErrors.nationality && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.nationality}</p>
                )}
              </div>
              )}

              {/* Step 5: Phone */}
              {currentStep === 5 && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  {t('register.phone')} <span className="text-gray-500 text-xs">({t('common.optional')})</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative" style={{ width: '120px', minWidth: '120px' }}>
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="w-full px-2 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white text-xs h-full appearance-none"
                      style={{
                        backgroundColor: 'rgba(29, 42, 59, 0.7)',
                        border: '1px solid rgba(213, 181, 119, 0.3)',
                        height: '42px',
                        backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%23D5B577\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.4rem center',
                        backgroundSize: '1em 1em',
                        paddingRight: '1.5rem'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#D5B577';
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }}
                    >
                      <option value="+20">🇪🇬 +20</option>
                      <option value="+966">🇸🇦 +966</option>
                      <option value="+971">🇦🇪 +971</option>
                      <option value="+965">🇰🇼 +965</option>
                      <option value="+974">🇶🇦 +974</option>
                      <option value="+968">🇴🇲 +968</option>
                      <option value="+973">🇧🇭 +973</option>
                      <option value="+962">🇯🇴 +962</option>
                      <option value="+961">🇱🇧 +961</option>
                      <option value="+963">🇸🇾 +963</option>
                      <option value="+964">🇮🇶 +964</option>
                      <option value="+970">🇵🇸 +970</option>
                      <option value="+967">🇾🇪 +967</option>
                      <option value="+249">🇸🇩 +249</option>
                      <option value="+218">🇱🇾 +218</option>
                      <option value="+216">🇹🇳 +216</option>
                      <option value="+213">🇩🇿 +213</option>
                      <option value="+212">🇲🇦 +212</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+1">🇨🇦 +1</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+61">🇦🇺 +61</option>
                    </select>
                  </div>
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
          </div>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.phone ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      height: '42px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#D5B577';
                      e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                    }}
                    onBlur={(e) => {
                      handleBlur(e);
                      if (!fieldErrors.phone) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                        e.target.style.backgroundColor = 'rgba(29, 42, 59, 0.7)';
                      }
                    }}
            />
          </div>
                </div>
                {fieldErrors.phone && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.phone}</p>
                )}
              </div>
              )}

              {/* Terms & Conditions - Only show on last step */}
              {currentStep === totalSteps && (
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 mt-0.5 rounded"
                    style={{ accentColor: '#D5B577' }}
                    required
                  />
                  <label className="ml-2 text-xs text-gray-300 leading-tight">
                    I agree to the{' '}
                    <Link to="/terms/client" className="underline" style={{ color: '#D5B577' }}>
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="underline" style={{ color: '#D5B577' }}>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-3 pt-2">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePrevious}
                    className="flex-1 py-3 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-base border-2"
                    style={{ 
                      borderColor: '#D5B577', 
                      color: '#D5B577',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgba(213, 181, 119, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    Previous
                  </button>
                )}
                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="flex-1 py-3 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-base"
                    style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c4a366'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#D5B577'}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-base disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                    onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#c4a366')}
                    onMouseLeave={(e) => (e.target.style.backgroundColor = '#D5B577')}
                  >
                    {loading ? t('common.loading') || 'Loading...' : t('register.createAccount')}
                  </button>
                )}
              </div>
        </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                {t('register.haveAccount')}{' '}
                <Link
                  to="/login"
                  className="font-semibold transition-colors"
                  style={{ color: '#D5B577' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  {t('register.signIn')}
                </Link>
        </p>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
